/* F24 — PDP scroll animations + pull-quote injection
   IntersectionObserver-based reveal + staggered cards + number count-up.
   Plus: inserts editorial pull-quote inside body prose after the Nth <p>.
   Mobile-first. Respects prefers-reduced-motion. Idempotent.
*/
(function () {
  if (typeof window === 'undefined') return;
  if (window.__F24_PDP_INITED__) return;
  window.__F24_PDP_INITED__ = true;

  document.documentElement.classList.add('f24-js');

  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var observer = null;

  function ready(fn) {
    if (document.readyState !== 'loading') return fn();
    document.addEventListener('DOMContentLoaded', fn);
  }

  function formatNumber(n, decimals) {
    if (decimals > 0) return n.toFixed(decimals);
    try { return Math.round(n).toLocaleString('es-MX'); }
    catch (e) { return String(Math.round(n)); }
  }

  function animateCount(el) {
    if (el.dataset.counted === '1') return;
    el.dataset.counted = '1';
    var target = parseFloat(el.getAttribute('data-count'));
    if (isNaN(target)) return;
    var raw = el.getAttribute('data-count');
    var decimals = (raw.split('.')[1] || '').length;
    var duration = parseInt(el.getAttribute('data-count-duration') || '1100', 10);
    if (reduceMotion) {
      var tn0 = el.firstChild;
      if (tn0 && tn0.nodeType === 3) tn0.nodeValue = formatNumber(target, decimals);
      return;
    }
    var textNode = el.firstChild && el.firstChild.nodeType === 3 ? el.firstChild : null;
    if (!textNode) {
      textNode = document.createTextNode('');
      el.insertBefore(textNode, el.firstChild);
    }
    var start = performance.now();
    function tick(now) {
      var t = Math.min((now - start) / duration, 1);
      var eased = 1 - Math.pow(1 - t, 3);
      textNode.nodeValue = formatNumber(target * eased, decimals);
      if (t < 1) requestAnimationFrame(tick);
      else textNode.nodeValue = formatNumber(target, decimals);
    }
    textNode.nodeValue = formatNumber(0, decimals);
    requestAnimationFrame(tick);
  }

  function revealStaggerChildren(el) {
    var children = el.querySelectorAll(':scope > .f24-anim-child');
    var delay = parseInt(el.getAttribute('data-stagger') || '90', 10);
    children.forEach(function (child, i) {
      setTimeout(function () { child.classList.add('f24-anim--in'); }, i * delay);
    });
  }

  // -------- Inject editorial pull-quote after the Nth <p> in body prose --------
  function injectPullQuotes(root) {
    var scope = root || document;
    scope.querySelectorAll('[data-pull-quote]').forEach(function (host) {
      if (host.dataset.pqInjected === '1') return;
      host.dataset.pqInjected = '1';

      var text = host.getAttribute('data-pull-quote');
      if (!text || !text.trim()) return;
      var after = parseInt(host.getAttribute('data-pull-after') || '2', 10);

      var paragraphs = host.querySelectorAll(':scope > p');
      if (!paragraphs.length) return;
      var insertAfter = paragraphs[Math.min(after - 1, paragraphs.length - 1)];

      var quote = document.createElement('aside');
      quote.className = 'f24-pull-quote';
      quote.setAttribute('role', 'note');

      var mark = document.createElement('span');
      mark.className = 'f24-pull-quote__mark';
      mark.setAttribute('aria-hidden', 'true');
      mark.textContent = '“';

      var p = document.createElement('p');
      p.className = 'f24-pull-quote__text';
      p.textContent = text;

      quote.appendChild(mark);
      quote.appendChild(p);
      insertAfter.parentNode.insertBefore(quote, insertAfter.nextSibling);

      // Observe for reveal (or show immediately if no observer / reduced motion)
      if (reduceMotion || !observer) {
        quote.classList.add('f24-anim--in');
      } else {
        observer.observe(quote);
      }
    });
  }

  function applyImmediate(animTargets, countTargets) {
    animTargets.forEach(function (el) { el.classList.add('f24-anim--in'); });
    document.querySelectorAll('.f24-anim-child').forEach(function (el) {
      el.classList.add('f24-anim--in');
    });
    document.querySelectorAll('.f24-pull-quote').forEach(function (el) {
      el.classList.add('f24-anim--in');
    });
    countTargets.forEach(animateCount);
  }

  ready(function () {
    var animTargets = document.querySelectorAll('.f24-anim');
    var countTargets = document.querySelectorAll('[data-count]');

    if (reduceMotion || !('IntersectionObserver' in window)) {
      injectPullQuotes();
      applyImmediate(animTargets, countTargets);
      return;
    }

    observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var el = entry.target;
        el.classList.add('f24-anim--in');

        if (el.hasAttribute && el.hasAttribute('data-count')) animateCount(el);
        var inner = el.querySelectorAll && el.querySelectorAll('[data-count]');
        if (inner) inner.forEach(animateCount);

        if (el.classList.contains('f24-anim--stagger')) {
          revealStaggerChildren(el);
        }
        observer.unobserve(el);
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -10% 0px' });

    // Inject pull-quotes (observer-aware)
    injectPullQuotes();

    animTargets.forEach(function (el) { observer.observe(el); });
    countTargets.forEach(function (el) {
      if (!el.closest('.f24-anim')) observer.observe(el);
    });
    // Defensive: observe orphan staggers that lack .f24-anim (otherwise children stay opacity:0)
    document.querySelectorAll('.f24-anim--stagger:not(.f24-anim)').forEach(function (el) {
      observer.observe(el);
    });
  });

  // Re-init on Shopify section reload (theme editor)
  document.addEventListener('shopify:section:load', function () {
    injectPullQuotes();
    document.querySelectorAll('.f24-anim:not(.f24-anim--in)').forEach(function (el) {
      el.classList.add('f24-anim--in');
    });
    document.querySelectorAll('.f24-anim-child:not(.f24-anim--in)').forEach(function (el) {
      el.classList.add('f24-anim--in');
    });
    document.querySelectorAll('.f24-pull-quote:not(.f24-anim--in)').forEach(function (el) {
      el.classList.add('f24-anim--in');
    });
    document.querySelectorAll('[data-count]').forEach(animateCount);
  });
})();
